import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";
import {
  Link2,
  Link2Off,
  MapPin,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Repeat,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DEFAULT_SETTINGS,
  GuidesOverlay,
  SettingsMenu,
  type SettingsState,
} from "./settings-menu";
import { AnnotationCanvas } from "./annotation-canvas";
import { SavedAnnotationsOverlay } from "./saved-annotations-overlay";
import { AnchorPinOverlay } from "./anchor-pin-overlay";
import { AnchorPinsDisplay } from "./anchor-pins-display";
import { useAnnotation } from "./annotation-context";
import {
  IconButton,
  PlaybackSpeedControl,
  TimecodeControl,
  VolumeControl,
  TIMECODE_FORMAT_KEY,
  type Speed,
  type TimecodeFormat,
} from "./video-player";
import type { WatchComment } from "@/hooks/use-watch";

/**
 * DualPlayer (2026-07-18) — side-by-side compare view of two video assets.
 *
 * Bottom-bar parity with the single VideoPlayer (2026-07-18 refactor):
 *   LEFT   → play / loop / playback-speed / volume
 *   CENTER → timecode (Standard/Timecode toggle) + sync toggle
 *   RIGHT  → anchor pin / settings gear / fullscreen
 *
 * Sync toggle:
 *   ON  — play/pause/seek/rate on either side mirror to the other.
 *   OFF — each side operates independently. Bottom-bar controls target the
 *         "active" side (the last one clicked); the other side keeps state.
 *
 * Volume, anchor pin, settings, annotation overlays all target the ACTIVE
 * side. Clicking a video sets it as active. An active-side hint is drawn as
 * a small primary bar under that side's title in the header (no big ring
 * around the canvas — user's 2026-07-18 spec).
 *
 * Image/audio compare is out of scope for v1 — SelectionActionBar's Compare
 * button only shows for exactly two `assetType==='video'` selections.
 */

type Side = "left" | "right";

export type DualPlayerHandle = {
  play: () => void;
  pause: () => void;
  seek: (seconds: number, side?: Side) => void;
  getCurrentTime: (side: Side) => number;
};

type Props = {
  leftSrc: string | null | undefined;
  rightSrc: string | null | undefined;
  leftTitle: string;
  rightTitle: string;

  /** Per-side comments — anchor pins + saved-annotations overlay reads these. */
  leftComments?: WatchComment[];
  rightComments?: WatchComment[];

  /** Version ids per side — needed by AnchorPinOverlay when the user drops a pin. */
  leftVersionId?: string | null;
  rightVersionId?: string | null;

  /** Comment-hook invalidation keys per side. Same shape as watch.tsx passes. */
  leftWatchKey?: string;
  rightWatchKey?: string;

  /** Fires when a side is activated (clicked). Parent uses this to swap the right-panel feed. */
  onActiveSideChange?: (side: Side) => void;
  /** Fires when the × on a side header is clicked. Parent decides where to navigate. */
  onCloseSide?: (side: Side) => void;
};

export const DualPlayer = forwardRef(function DualPlayer(
  {
    leftSrc,
    rightSrc,
    leftTitle,
    rightTitle,
    leftComments = [],
    rightComments = [],
    leftVersionId = null,
    rightVersionId = null,
    leftWatchKey,
    rightWatchKey,
    onActiveSideChange,
    onCloseSide,
  }: Props,
  ref: Ref<DualPlayerHandle>,
) {
  const leftRef = useRef<HTMLVideoElement>(null);
  const rightRef = useRef<HTMLVideoElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const annotation = useAnnotation();

  const [synced, setSynced] = useState(true);
  const [activeSide, setActiveSide] = useState<Side>("left");
  const [isPlaying, setIsPlaying] = useState(false);
  const [rate, setRate] = useState<Speed>(1);
  const [looping, setLooping] = useState(false);
  const [currentTime, setCurrentTime] = useState({ left: 0, right: 0 });
  const [duration, setDuration] = useState({ left: 0, right: 0 });
  // Per-side volume/mute state. Volume control targets the ACTIVE side; the
  // other stays as-is. Default: right side starts muted so the user isn't hit
  // by double audio when compare loads.
  const [leftVolume, setLeftVolume] = useState(1);
  const [rightVolume, setRightVolume] = useState(1);
  const [leftMuted, setLeftMuted] = useState(false);
  const [rightMuted, setRightMuted] = useState(true);
  const [settings, setSettings] = useState<SettingsState>(DEFAULT_SETTINGS);
  const [timecodeFormat, setTimecodeFormat] = useState<TimecodeFormat>(() => {
    if (typeof window === "undefined") return "timecode";
    const stored = window.localStorage.getItem(TIMECODE_FORMAT_KEY);
    return stored === "standard" ? "standard" : "timecode";
  });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Persist timecode format across sessions — same key as VideoPlayer so a
  // user's preference carries over from watch pages.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(TIMECODE_FORMAT_KEY, timecodeFormat);
  }, [timecodeFormat]);

  // Guard against feedback loops during sync — when the effect sets side B's
  // currentTime after side A seeks, we don't want B's onSeeked handler to
  // re-fire the same sync in the other direction. Set the flag before every
  // programmatic assignment, clear on the next tick.
  const programmatic = useRef(false);
  function withProgrammatic<T>(fn: () => T): T {
    programmatic.current = true;
    try {
      return fn();
    } finally {
      queueMicrotask(() => {
        programmatic.current = false;
      });
    }
  }

  const setSide = useCallback(
    (side: Side) => {
      setActiveSide(side);
      onActiveSideChange?.(side);
    },
    [onActiveSideChange],
  );

  // Bind <video> event listeners for both sides. Sync mirrors play/pause/
  // seek/rate to the other side when synced; timeupdate keeps the two clocks
  // aligned within 0.4s. The isPlaying flag reflects the active side (single
  // bottom bar).
  useEffect(() => {
    const bind = (v: HTMLVideoElement | null, side: Side) => {
      if (!v) return () => {};
      const onPlay = () => {
        if (side === activeSide) setIsPlaying(true);
        if (synced && !programmatic.current) {
          const other = side === "left" ? rightRef.current : leftRef.current;
          if (other && other.paused) withProgrammatic(() => void other.play().catch(() => {}));
        }
      };
      const onPause = () => {
        if (side === activeSide) setIsPlaying(false);
        if (synced && !programmatic.current) {
          const other = side === "left" ? rightRef.current : leftRef.current;
          if (other && !other.paused) withProgrammatic(() => other.pause());
        }
      };
      const onTime = () => {
        setCurrentTime((prev) => ({ ...prev, [side]: v.currentTime }));
        if (synced && !programmatic.current) {
          const other = side === "left" ? rightRef.current : leftRef.current;
          if (other && Math.abs(other.currentTime - v.currentTime) > 0.4) {
            withProgrammatic(() => {
              other.currentTime = v.currentTime;
            });
          }
        }
      };
      const onDur = () => {
        setDuration((prev) => ({ ...prev, [side]: Number.isFinite(v.duration) ? v.duration : 0 }));
      };
      const onRate = () => {
        setRate(v.playbackRate as Speed);
      };
      const onVolChange = () => {
        if (side === "left") {
          setLeftVolume(v.volume);
          setLeftMuted(v.muted);
        } else {
          setRightVolume(v.volume);
          setRightMuted(v.muted);
        }
      };
      v.addEventListener("play", onPlay);
      v.addEventListener("pause", onPause);
      v.addEventListener("timeupdate", onTime);
      v.addEventListener("durationchange", onDur);
      v.addEventListener("ratechange", onRate);
      v.addEventListener("volumechange", onVolChange);
      return () => {
        v.removeEventListener("play", onPlay);
        v.removeEventListener("pause", onPause);
        v.removeEventListener("timeupdate", onTime);
        v.removeEventListener("durationchange", onDur);
        v.removeEventListener("ratechange", onRate);
        v.removeEventListener("volumechange", onVolChange);
      };
    };
    const unLeft = bind(leftRef.current, "left");
    const unRight = bind(rightRef.current, "right");
    return () => {
      unLeft();
      unRight();
    };
  }, [synced, activeSide]);

  // Reflect loop into both DOM elements — loop is a "playback intent" that
  // applies to both regardless of sync.
  useEffect(() => {
    if (leftRef.current) leftRef.current.loop = looping;
    if (rightRef.current) rightRef.current.loop = looping;
  }, [looping]);

  // Rate propagates to both — matches "same tempo comparison" intent.
  useEffect(() => {
    if (leftRef.current) leftRef.current.playbackRate = rate;
    if (rightRef.current) rightRef.current.playbackRate = rate;
  }, [rate]);

  // Reflect per-side mute + volume into DOM.
  useEffect(() => {
    if (leftRef.current) {
      leftRef.current.muted = leftMuted;
      leftRef.current.volume = leftVolume;
    }
  }, [leftMuted, leftVolume]);
  useEffect(() => {
    if (rightRef.current) {
      rightRef.current.muted = rightMuted;
      rightRef.current.volume = rightVolume;
    }
  }, [rightMuted, rightVolume]);

  const togglePlay = useCallback(() => {
    const active = activeSide === "left" ? leftRef.current : rightRef.current;
    if (!active) return;
    if (active.paused) active.play().catch(() => {});
    else active.pause();
  }, [activeSide]);

  // Click on a canvas:
  //   - If it's already the active side → toggle play/pause on it.
  //   - If it's the OTHER side → just activate it (don't touch play state on
  //     either side). User's spec 2026-07-18: "if the user click on the other
  //     video it should not stop playing".
  const handleCanvasClick = useCallback(
    (side: Side) => {
      if (side !== activeSide) {
        setSide(side);
        return;
      }
      togglePlay();
    },
    [activeSide, setSide, togglePlay],
  );

  // Space → toggle play/pause on active side. Same input-skip guard as single
  // VideoPlayer so typing spaces in the composer doesn't hijack playback.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        togglePlay();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [togglePlay]);

  const seekActive = useCallback(
    (t: number) => {
      const active = activeSide === "left" ? leftRef.current : rightRef.current;
      if (!active) return;
      active.currentTime = t;
      if (synced) {
        const other = activeSide === "left" ? rightRef.current : leftRef.current;
        if (other) {
          withProgrammatic(() => {
            other.currentTime = t;
          });
        }
      }
    },
    [activeSide, synced],
  );

  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        (activeSide === "left" ? leftRef.current : rightRef.current)?.play().catch(() => {});
      },
      pause: () => {
        (activeSide === "left" ? leftRef.current : rightRef.current)?.pause();
      },
      seek: (t, side) => {
        const target = side
          ? side === "left"
            ? leftRef.current
            : rightRef.current
          : activeSide === "left"
            ? leftRef.current
            : rightRef.current;
        if (target) target.currentTime = t;
      },
      getCurrentTime: (side) => currentTime[side],
    }),
    [activeSide, currentTime],
  );

  // Fullscreen for the WHOLE compare surface (both videos together).
  useEffect(() => {
    function onChange() {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    }
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (!rootRef.current) return;
    if (document.fullscreenElement === rootRef.current) document.exitFullscreen().catch(() => {});
    else rootRef.current.requestFullscreen().catch(() => {});
  }, []);

  // Video style — reflects shared zoom setting into both canvases so a Fit /
  // Fill / Zoom In/Out change applies symmetrically.
  const videoStyle = useMemo<React.CSSProperties>(() => {
    const baseFit: React.CSSProperties = { maxHeight: "100%", maxWidth: "100%" };
    if (settings.zoomMode === "fill") {
      return { objectFit: "cover" as const, width: "100%", height: "100%" };
    }
    if (settings.zoomMode === "custom") {
      return { ...baseFit, transform: `scale(${settings.zoomScale})`, transformOrigin: "center" };
    }
    return baseFit;
  }, [settings.zoomMode, settings.zoomScale]);

  // Volume control targets active side.
  const activeVolume = activeSide === "left" ? leftVolume : rightVolume;
  const activeMuted = activeSide === "left" ? leftMuted : rightMuted;
  const setActiveVolume = (v: number) => {
    const target = activeSide === "left" ? leftRef.current : rightRef.current;
    if (!target) return;
    target.volume = v;
    target.muted = v === 0;
  };
  const toggleActiveMute = () => {
    const target = activeSide === "left" ? leftRef.current : rightRef.current;
    if (!target) return;
    target.muted = !target.muted;
    if (!target.muted && target.volume === 0) target.volume = 0.5;
  };

  const activeTime = currentTime[activeSide];
  const activeDuration = duration[activeSide];

  return (
    <div ref={rootRef} className="relative flex min-h-0 w-full flex-1 flex-col bg-black">
      {/* Side headers — centered filename + close ×. Active side shows a small
          primary bar directly under the filename (no ring around the canvas). */}
      <div className="grid shrink-0 grid-cols-2 gap-px bg-black/10 dark:bg-white/5">
        <SideHeader
          title={leftTitle}
          active={activeSide === "left"}
          onClick={() => setSide("left")}
          onClose={onCloseSide ? () => onCloseSide("left") : undefined}
        />
        <SideHeader
          title={rightTitle}
          active={activeSide === "right"}
          onClick={() => setSide("right")}
          onClose={onCloseSide ? () => onCloseSide("right") : undefined}
        />
      </div>

      {/* Video canvases with per-side annotation overlays. AnchorPinOverlay
          is only mounted on the ACTIVE side — the drop cursor and the
          floating composer follow whichever video you last clicked. Saved
          pins + saved annotations render on both sides using their own
          per-side comment lists. */}
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-px bg-black/10 dark:bg-white/5">
        <SideCanvas
          videoRef={leftRef}
          src={leftSrc}
          videoStyle={videoStyle}
          settings={settings}
          onClick={() => handleCanvasClick("left")}
          isActive={activeSide === "left"}
          comments={leftComments}
          versionId={leftVersionId}
          watchKey={leftWatchKey}
          getCurrentTime={() => leftRef.current?.currentTime ?? 0}
          onSeek={(t) => {
            if (leftRef.current) leftRef.current.currentTime = t;
          }}
        />
        <SideCanvas
          videoRef={rightRef}
          src={rightSrc}
          videoStyle={videoStyle}
          settings={settings}
          onClick={() => handleCanvasClick("right")}
          isActive={activeSide === "right"}
          comments={rightComments}
          versionId={rightVersionId}
          watchKey={rightWatchKey}
          getCurrentTime={() => rightRef.current?.currentTime ?? 0}
          onSeek={(t) => {
            if (rightRef.current) rightRef.current.currentTime = t;
          }}
        />
      </div>

      {/* Shared bottom bar — parity with single VideoPlayer plus the sync
          toggle. Controls target the ACTIVE side; sync mirrors them to the
          other side. */}
      <div className="shrink-0 border-t border-black/5 bg-white text-foreground dark:border-white/5 dark:bg-black dark:text-white">
        {/* Scrubber for the active side */}
        <div className="px-4 pt-2">
          <input
            type="range"
            min={0}
            max={activeDuration || 0}
            step={0.01}
            value={activeTime}
            onChange={(e) => seekActive(Number(e.target.value))}
            className="h-1 w-full cursor-pointer accent-primary"
            aria-label={`Scrub ${activeSide}`}
          />
        </div>

        <div className="flex items-center gap-2 px-4 pb-2 pt-0">
          {/* LEFT — play / loop / speed / volume */}
          <div className="flex items-center gap-1">
            <IconButton onClick={togglePlay} label={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </IconButton>
            <IconButton
              onClick={() => setLooping((v) => !v)}
              active={looping}
              label={looping ? "Loop on" : "Loop off"}
            >
              <Repeat className="h-4 w-4" />
            </IconButton>
            <PlaybackSpeedControl value={rate} onChange={setRate} />
            <VolumeControl
              volume={activeMuted ? 0 : activeVolume}
              muted={activeMuted}
              onToggleMute={toggleActiveMute}
              onChange={setActiveVolume}
            />
          </div>

          {/* CENTER — timecode + sync toggle. Sync sits directly next to the
              timecode so users know it's a compare-specific control tied to
              playback. */}
          <div className="flex flex-1 items-center justify-center gap-2">
            <TimecodeControl
              currentTime={activeTime}
              duration={activeDuration}
              format={timecodeFormat}
              onFormatChange={setTimecodeFormat}
            />
            <IconButton
              onClick={() => setSynced((v) => !v)}
              active={synced}
              label={synced ? "Playback linked (unlink to control sides independently)" : "Playback unlinked (link to control both together)"}
            >
              {synced ? <Link2 className="h-4 w-4" /> : <Link2Off className="h-4 w-4" />}
            </IconButton>
          </div>

          {/* RIGHT — anchor pin / settings / fullscreen. Anchor + settings
              target the active side; fullscreen wraps both. */}
          <div className="flex items-center gap-1">
            <IconButton
              onClick={() => {
                if (annotation.anchorMode) annotation.deactivateAnchor();
                else annotation.activateAnchor();
              }}
              active={annotation.anchorMode}
              label="Drop anchor pin"
            >
              <MapPin className="h-4 w-4" />
            </IconButton>
            <SettingsMenu value={settings} onChange={setSettings}>
              <Settings className="h-4 w-4" />
            </SettingsMenu>
            <IconButton
              onClick={toggleFullscreen}
              label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </IconButton>
          </div>
        </div>
      </div>

      {/* AnchorPinOverlay is HERE (outside the canvas grid) so its floating
          composer can position absolutely without the grid clipping it. But
          the click-capture layer is only meaningful on the active side, so
          we render just one instance and it wires to the active side's
          version + watch key. Rendered near the ROOT via portal-like sibling
          — the overlay itself does the click-capture positioning by covering
          its parent. Since we're mounting it as a sibling to the grid, we
          need the grid's client box to be its bounding box.

          Wait — AnchorPinOverlay covers `inset-0` of its parent. We keep it
          inside SideCanvas above so it constrains to the active side's video
          box only. This block is intentionally left as a no-op placeholder;
          see SideCanvas for the actual mount. */}
    </div>
  );
});

// ── Per-side canvas w/ annotation stack ─────────────────────────────────────

/**
 * SideCanvas encapsulates one video element + its per-side annotation
 * overlays. Splitting this out keeps the top-level DualPlayer render
 * readable and avoids duplicating the same overlay stack twice inline.
 *
 * The AnchorPinOverlay only mounts when this side is active — the annotation
 * context is global, so mounting on both sides would let a click on either
 * side drop a pin. Active-only gating solves that cleanly.
 */
function SideCanvas({
  videoRef,
  src,
  videoStyle,
  settings,
  onClick,
  isActive,
  comments,
  versionId,
  watchKey,
  getCurrentTime,
  onSeek,
}: {
  videoRef: React.Ref<HTMLVideoElement>;
  src: string | null | undefined;
  videoStyle: React.CSSProperties;
  settings: SettingsState;
  onClick: () => void;
  isActive: boolean;
  comments: WatchComment[];
  versionId: string | null;
  watchKey: string | undefined;
  getCurrentTime: () => number;
  onSeek: (t: number) => void;
}) {
  const annotation = useAnnotation();
  return (
    <div
      className="relative flex min-h-0 items-center justify-center overflow-hidden bg-black"
      onClick={onClick}
    >
      {src ? (
        <video ref={videoRef} src={src} preload="metadata" playsInline style={videoStyle} />
      ) : (
        <div className="text-sm text-white/40">No source</div>
      )}

      <GuidesOverlay ratio={settings.guidesRatio} showMask={settings.showMask} />

      {/* Live draw surface for the active side. Both sides can carry saved
          annotations, but only the active side accepts new draws. */}
      {isActive && (
        <AnnotationCanvas visible={annotation.isActive || annotation.shapes.length > 0} />
      )}

      {/* Saved annotations overlay — reads the side's comment list. Filters by
          currentTime proximity (video/audio semantics). */}
      <SavedAnnotationsOverlay
        visible={settings.viewAnnotations && !annotation.isActive}
        comments={comments}
        currentTime={getCurrentTime()}
      />

      {/* Anchor-pin drop overlay — active side only. */}
      {isActive && (
        <AnchorPinOverlay
          mode="workspace"
          watchKey={watchKey}
          currentVersionId={versionId}
          getCurrentTime={getCurrentTime}
        />
      )}

      {/* Persistent anchor pins for existing comments — both sides. */}
      <AnchorPinsDisplay
        visible={settings.viewAnchoredComments && !annotation.anchorMode}
        comments={comments}
        onSeek={onSeek}
      />
    </div>
  );
}

// ── Side header ─────────────────────────────────────────────────────────────

function SideHeader({
  title,
  active,
  onClick,
  onClose,
}: {
  title: string;
  active: boolean;
  onClick: () => void;
  onClose?: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      className={cn(
        "relative flex items-center bg-white px-3 py-2 text-sm text-black/70 transition-colors dark:bg-black dark:text-white/70",
        active ? "text-foreground dark:text-white" : "hover:text-black/90 dark:hover:text-white/90",
      )}
    >
      {onClose && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close side"
          className="absolute left-3 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded text-black/60 transition-colors hover:bg-black/10 hover:text-black dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
        >
          ×
        </button>
      )}

      <span className="mx-auto min-w-0 max-w-[80%] truncate text-center">
        {title}
      </span>

      {active && (
        <span
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-0.5 bg-primary"
        />
      )}
    </div>
  );
}
